import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { capitalizeFirstLetter, formatMoney } from '@/shared/lib/format/money';
import { StatusPill } from '../WalletPagePrimitives';
import { getTxnType } from '../walletPage.constants';
import {
  asDisplayText,
  asOptionalText,
  fmtDisplayDateTime,
} from '../walletPage.utils';
import type { Txn } from '../WalletPage.types';

type TransactionDetailModalProps = {
  open: boolean;
  txn: Txn | null;
  onClose: () => void;
};

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-3 border-b border-[var(--dash-line)] py-2.5 last:border-0 sm:grid-cols-[140px_1fr]">
      <dt className="text-[11px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase">
        {label}
      </dt>
      <dd className="m-0 text-sm text-[var(--dash-ink)]">{children}</dd>
    </div>
  );
}

export function TransactionDetailModal({
  open,
  txn,
  onClose,
}: TransactionDetailModalProps) {
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open || !txn) return null;

  const reason = asOptionalText(txn.reason);
  const description = asDisplayText(
    txn.description,
    'No description provided.',
  );
  const notes = asOptionalText(txn.notes);
  const campaign =
    asOptionalText(txn.campaignName) || asOptionalText(txn.campaignId);
  const status = asOptionalText(txn.paymentStatus);
  const createdAt = asOptionalText(txn.createdAt);
  const updatedAt = asOptionalText(txn.updatedAt);
  const txnId = asDisplayText(txn._id, '—');

  const balanceAfter =
    txn.balanceAfter != null
      ? txn.balanceAfter
      : txn.availableBalance != null
        ? txn.availableBalance
        : null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="Close transaction details"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="txn-detail-title"
        className="relative z-10 flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-[var(--dash-line)] bg-white shadow-xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-[var(--dash-line)] px-5 py-4">
          <div className="text-left">
            <h2
              id="txn-detail-title"
              className="m-0 text-base font-bold text-[var(--dash-ink)]"
            >
              Transaction details
            </h2>
            <p className="mt-1 mb-0 text-xs text-[var(--dash-muted)]">
              {getTxnType(reason)} · {fmtDisplayDateTime(createdAt)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-[var(--dash-muted)] hover:bg-[var(--dash-soft)]"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 text-left">
          <dl className="m-0">
            <DetailRow label="Reason">{getTxnType(reason)}</DetailRow>
            <DetailRow label="Status">
              <StatusPill value={status} asIs />
            </DetailRow>
            <DetailRow label="Amount">
              <span className="font-semibold">{formatMoney(txn.amount)}</span>
            </DetailRow>
            <DetailRow label="Description">
              <p className="m-0 leading-relaxed break-words whitespace-pre-wrap text-[var(--dash-ink)]">
                {description}
              </p>
            </DetailRow>
            {notes ? (
              <DetailRow label="Notes">
                <p className="m-0 leading-relaxed break-words whitespace-pre-wrap">
                  {notes}
                </p>
              </DetailRow>
            ) : null}
            {campaign ? <DetailRow label="Campaign">{campaign}</DetailRow> : null}
            {/* {reference ? (
              <DetailRow label="Reference">
                <span className="break-all font-mono text-xs">{reference}</span>
              </DetailRow>
            ) : null} */}
            <DetailRow label="Transaction ID">
              <span className="break-all font-mono text-xs">{txnId}</span>
            </DetailRow>
            {reason ? (
              <DetailRow label="Type">
                <span className="font-mono text-xs">{capitalizeFirstLetter(txn?.type as string)}</span>
              </DetailRow>
            ) : null}
            {balanceAfter != null ? (
              <DetailRow label="Balance after">
                {formatMoney(balanceAfter)}
              </DetailRow>
            ) : null}
            <DetailRow label="Date & time">
              {fmtDisplayDateTime(createdAt)}
            </DetailRow>
            {updatedAt ? (
              <DetailRow label="Updated">
                {fmtDisplayDateTime(updatedAt)}
              </DetailRow>
            ) : null}
          </dl>
        </div>

        <div className="border-t border-[var(--dash-line)] px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-lg border border-[var(--dash-line)] py-2 text-sm font-medium text-[var(--dash-muted)] hover:bg-[var(--dash-soft)]"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
